import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import styled from "styled-components";

import type { PlanogramContainer } from "@CommonTypes/merchflow/pog/container";
import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { PlanogramPegboardPlacement } from "@CommonTypes/merchflow/pog/pegboard";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { useUser } from "src/atoms/user";
import { useMenu } from "src/components/Menu";
import {
  labelOptionAtom,
  planogramHoveredContainerAtom,
  planogramHoveredItemAtom,
  planogramHoveredPegboardPlacementAtom,
  planogramHoveredShelfAtom,
  planogramImagesModeAtom,
  planogramKonvaRendererAtom,
  planogramSelectedItemsAtom,
  planogramSelectedShelfAtom,
  planogramSelectedShelvesAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { usePogDeleteProductModal } from "src/modals/PogDeleteProduct/store/hooks";
import { KeyCode } from "src/utils/keys";
import { useTranslation } from "src/i18n";
import {
  createPlanogramAssetProvider,
  usePlanogramRendererLabels,
} from "../../common/konvaRendererAdapter";
import { sideViewAtom } from "../../store/atoms";
import { usePogAtomActions } from "../../store/actions";
import type { PogAtom } from "../../store/types";
import {
  areShelvesInSameMergedGroup,
  getNewShelfNotchNo,
  resolveDraggedItems,
} from "../../store/utils";
import { getActiveDragPayload, onDropOverToCanvas } from "../Item/store/utils";
import type { DraggedItemsPayload } from "../Item/store/types";
import { usePlanogramPanel } from "../Panel/store/hooks";
import { useShelfContextMenu } from "../Shelf/useShelfContextMenu";
import {
  createItemRangeOperation,
  createPlanogramSceneProjector,
  createViewportModel,
  emptyPlanogramSelection,
  evaluateStandardOperation,
  hitTestProduct,
  hitTestShelfRow,
  KonvaPlanogram,
  type ItemRangeTarget,
  type OverlayChannelInput,
  type PlanogramContextRequest,
  type PlanogramDropPreview,
  type PlanogramEntityRef,
  type PlanogramOverlayBadgeRequest,
  type PlanogramProductLabel,
  type PlanogramScene,
  type PlanogramSelection,
  type ViewportPoint,
  type ViewportState,
} from "../KonvaPlanogram";

const EditorRoot = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
`;

const resolveRangeTarget = (
  scene: PlanogramScene,
  worldPoint: ViewportPoint,
  draggedItemIds: ReadonlySet<string>,
): ItemRangeTarget | null => {
  const targetProductRef = hitTestProduct(scene, worldPoint, draggedItemIds);
  const targetProduct = targetProductRef
    ? scene.products.find((candidate) => candidate.id === targetProductRef.id)
    : undefined;

  if (targetProduct) {
    return {
      itemId: targetProduct.id,
      kind: "item",
      side: worldPoint.x < targetProduct.x + targetProduct.width / 2 ? "left" : "right",
    };
  }

  const shelfRef = hitTestShelfRow(scene, worldPoint);
  return shelfRef ? { kind: "shelf", shelfId: shelfRef.id } : null;
};

const getDraggedItemWorldSize = (
  pog: Pog,
  payload: DraggedItemsPayload,
): { height: number; width: number } | null => {
  const item =
    resolveDraggedItems(pog, [payload.anchorItemId])[0] ??
    resolveDraggedItems(pog, [...payload.itemIds])[0];
  if (!item) return null;

  return {
    height: item.getHeight() * item.getVerticalFacings() + item.getYCappingHeight(),
    width: item.getWidth() * item.getHorizontalFacings() + item.getXCappingWidth(),
  };
};

const getDropPreviewRect = (
  scene: PlanogramScene,
  target: ItemRangeTarget,
  worldPoint: ViewportPoint,
  size: { height: number; width: number },
): PlanogramDropPreview | null => {
  if (target.kind === "item") {
    const product = scene.products.find((candidate) => candidate.id === target.itemId);
    if (!product) return null;
    return {
      height: size.height,
      width: size.width,
      x: target.side === "left" ? product.x - size.width : product.x + product.width,
      y: product.y + product.height - size.height,
    };
  }

  const shelf = scene.shelves.find((candidate) => candidate.id === target.shelfId);
  if (!shelf) return null;
  const maxX = shelf.x + Math.max(0, shelf.width - size.width);
  return {
    height: size.height,
    width: size.width,
    x: Math.min(Math.max(worldPoint.x - size.width / 2, shelf.x), maxX),
    y: shelf.y - size.height,
  };
};

interface Props {
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null;
  onOverlayBadgeActivate?: (request: PlanogramOverlayBadgeRequest) => void;
  overlayBadges?: ReadonlyMap<string, string>;
  overlayChannels?: readonly OverlayChannelInput[];
  pogAtom: PogAtom;
  scaleX: number;
  scaleY: number;
  scrollToEntityId?: string | null;
  setIsModified: () => void;
}

export const KonvaEditorPlanogram = ({
  availableStores,
  onOverlayBadgeActivate,
  overlayBadges,
  overlayChannels,
  pogAtom,
  scaleX,
  scaleY,
  scrollToEntityId,
  setIsModified,
}: Props) => {
  const { t } = useTranslation("common");
  const { user } = useUser();
  const [pog, setPog] = useAtom(pogAtom);
  const imagesMode = useAtomValue(planogramImagesModeAtom);
  const labelOption: PlanogramProductLabel = useAtomValue(labelOptionAtom);
  const setHoveredItem = useSetAtom(planogramHoveredItemAtom);
  const setHoveredPegboardPlacement = useSetAtom(planogramHoveredPegboardPlacementAtom);
  const setHoveredShelf = useSetAtom(planogramHoveredShelfAtom);
  const setHoveredContainer = useSetAtom(planogramHoveredContainerAtom);
  const sharedSelectedItems = useAtomValue(planogramSelectedItemsAtom);
  const sharedSelectedShelfIds = useAtomValue(planogramSelectedShelvesAtom);
  const setSelectedItems = useSetAtom(planogramSelectedItemsAtom);
  const setSelectedShelf = useSetAtom(planogramSelectedShelfAtom);
  const setSelectedShelfIds = useSetAtom(planogramSelectedShelvesAtom);
  const { setTab } = usePlanogramPanel();
  const { openMenu } = useMenu();
  const { openPogDeleteProductModal } = usePogDeleteProductModal();
  const setSideView = useSetAtom(sideViewAtom);
  const {
    addBayToSide,
    addItemNextToAnotherItem,
    addItemsToShelf,
    addNewShelf,
    deleteContainer,
    deleteBay,
    duplicateItemsNextToItem,
    duplicateItemsToShelf,
    flipItemsHorizontally,
    splitContainerBottom,
    splitContainerRight,
    unrangeContainer,
    unrangeItems,
  } = usePogAtomActions(pogAtom);
  const labels = usePlanogramRendererLabels();
  const openShelfContextMenu = useShelfContextMenu(pogAtom, () => setIsModified());
  const [sceneProjector] = useState(createPlanogramSceneProjector);
  const scene = useMemo(
    () => sceneProjector.project(pog, labelOption),
    [labelOption, pog, sceneProjector],
  );

  const baseScale = Math.max(0.01, Math.min(scaleX, scaleY));
  const merchantId = user?.merchantId ?? null;
  const assetProvider = useMemo(
    () => (imagesMode ? createPlanogramAssetProvider(merchantId) : undefined),
    [imagesMode, merchantId],
  );

  const viewportModel = useMemo(
    () =>
      createViewportModel({
        bounds: { maxScale: baseScale * 3, minScale: baseScale * 0.5 },
        defaultState: { scale: baseScale },
      }),
    [baseScale],
  );
  const [viewport, setViewport] = useState<ViewportState>(() => viewportModel.defaultState);
  useEffect(() => {
    setViewport(viewportModel.defaultState);
  }, [viewportModel]);

  const [selection, setSelection] = useState<PlanogramSelection>(() => {
    const productIds = sharedSelectedItems
      .filter((item): item is PlanogramItem => item instanceof PlanogramItem)
      .map((item) => item.uniqueId)
      .filter((id) => pog.getItem(id) instanceof PlanogramItem);
    if (productIds.length > 0) {
      return {
        anchorId: productIds[0] ?? null,
        focusedId: productIds.at(-1) ?? null,
        ids: productIds,
        kind: "product",
      };
    }

    const shelfIds = sharedSelectedShelfIds.filter((id) => pog.getShelf(id) !== null);
    if (shelfIds.length > 0) {
      return {
        anchorId: shelfIds[0] ?? null,
        focusedId: shelfIds.at(-1) ?? null,
        ids: shelfIds,
        kind: "shelf",
      };
    }

    return emptyPlanogramSelection;
  });

  const getSelectedPlanogramItems = (nextSelection: PlanogramSelection) => {
    if (nextSelection.kind === "product") {
      return nextSelection.ids
        .map((id) => pog.getItem(id))
        .filter((item): item is PlanogramItem => item instanceof PlanogramItem);
    }

    if (nextSelection.kind === "placement") {
      return nextSelection.ids
        .map((id) => pog.planogram.getPegboardPlacement(id))
        .filter((placement): placement is PlanogramPegboardPlacement => placement !== null);
    }

    return [];
  };

  const handleSelectionChange = (nextSelection: PlanogramSelection) => {
    setSelection(nextSelection);
    const items = getSelectedPlanogramItems(nextSelection);
    setSelectedItems(items);
    if (nextSelection.kind === "shelf") {
      const shelfIds = [...nextSelection.ids];
      setSelectedShelfIds(shelfIds);
      setSelectedShelf(shelfIds.length === 1 ? (pog.getShelf(shelfIds[0] ?? "") ?? null) : null);
      setTab(shelfIds.length === 1 ? "ADD_EDIT_FIXTURE" : "FIXTURES_DETAILS");
      return;
    }

    setSelectedShelfIds([]);
    setSelectedShelf(null);
    if (items.length > 0) setTab("PRODUCT_DETAILS");
  };

  const clearHover = () => {
    setHoveredItem(null);
    setHoveredPegboardPlacement(null);
    setHoveredShelf(null);
    setHoveredContainer(null);
  };

  const handleHoverChange = (entity: PlanogramEntityRef | null) => {
    if (entity?.kind === "product") {
      const item = pog.getItem(entity.id);
      clearHover();
      setHoveredItem(item instanceof PlanogramItem ? item : null);
      return;
    }

    if (entity?.kind === "placement") {
      clearHover();
      setHoveredPegboardPlacement(pog.planogram.getPegboardPlacement(entity.id) ?? null);
      return;
    }

    if (entity?.kind === "shelf") {
      clearHover();
      setHoveredShelf(pog.getShelf(entity.id) ?? null);
      return;
    }

    if (entity?.kind === "container") {
      clearHover();
      setHoveredContainer(
        pog.containers.find((candidate) => candidate.uniqueId === entity.id) ?? null,
      );
      return;
    }

    clearHover();
  };

  const handleActivate = (entity: PlanogramEntityRef, activatedSelection?: PlanogramSelection) => {
    if (entity.kind !== "shelf") return;
    const shelfIds =
      activatedSelection?.kind === "shelf" ? [...activatedSelection.ids] : [entity.id];
    if (activatedSelection) setSelection(activatedSelection);
    setSelectedItems([]);
    setSelectedShelfIds(shelfIds);
    setSelectedShelf(pog.getShelf(entity.id) ?? null);
    setTab("FIXTURES_DETAILS");
  };

  const resolveContextWorldPoint = (position: PlanogramContextRequest["position"]) => {
    if (!position) return null;

    const stageHost = rootRef.current?.querySelector<HTMLElement>(
      '[data-testid="konva-planogram"]',
    );
    const rect = stageHost?.getBoundingClientRect();
    if (!rect) return null;

    const stagePoint = {
      x: position.clientX - rect.left + (stageHost?.scrollLeft ?? 0),
      y: position.clientY - rect.top + (stageHost?.scrollTop ?? 0),
    };

    return {
      x: (stagePoint.x - viewport.offsetX) / viewport.scale,
      y: (stagePoint.y - viewport.offsetY) / viewport.scale,
    };
  };

  const openBayContextMenu = (
    bay: PlanogramScene["bays"][number],
    position: PlanogramContextRequest["position"],
  ) => {
    const domainBay = pog.getBay(bay.id);
    if (!domainBay) return;

    openMenu({
      event: createMenuAnchorEvent(position),
      menu: {
        title: t("planogramCanvas.bayMenu.title", { bayNo: bay.bayNo }),
        options: [
          {
            label: t("planogramCanvas.bayMenu.newShelf"),
            keybind: { keyCode: KeyCode.N },
            onClick: () => {
              const worldPoint = resolveContextWorldPoint(position);
              const notchNo = worldPoint
                ? getNewShelfNotchNo({
                    bayHeight: bay.height,
                    cursorY: worldPoint.y - bay.y,
                    maxNotch: bay.maxNotch,
                    notchSpacing: bay.notchSpacing,
                    scaleY: 1,
                  })
                : undefined;
              const newShelf = addNewShelf(bay.bayNo, notchNo);
              setSelectedShelf(newShelf);
              setSelectedShelfIds(newShelf ? [newShelf.uniqueId] : []);
              setTab("ADD_EDIT_FIXTURE");
            },
          },
          {
            label: t("planogramCanvas.bayMenu.addBayLeft"),
            keybind: { keyCode: KeyCode.Q },
            onClick: () => addBayToSide(domainBay, "LEFT"),
          },
          {
            label: t("planogramCanvas.bayMenu.addBayRight"),
            keybind: { keyCode: KeyCode.E },
            onClick: () => addBayToSide(domainBay, "RIGHT"),
          },
          {
            label: t("planogramCanvas.bayMenu.deleteBay"),
            keybind: { keyCode: KeyCode.DELETE },
            onClick: () => {
              const deletedShelfIds = new Set(domainBay.shelves.map((shelf) => shelf.uniqueId));
              setSelectedShelfIds((current) =>
                current.filter((shelfId) => !deletedShelfIds.has(shelfId)),
              );
              setSelectedShelf((current) =>
                current && deletedShelfIds.has(current.uniqueId) ? null : current,
              );
              deleteBay(domainBay);
            },
          },
        ],
      },
    });
  };

  const createMenuAnchorEvent = (position: PlanogramContextRequest["position"]) => ({
    clientX: position?.clientX ?? 0,
    clientY: position?.clientY ?? 0,
    preventDefault: () => undefined,
    stopPropagation: () => undefined,
    target: null,
  });

  const openItemContextMenu = (
    items: PlanogramItem[],
    position: PlanogramContextRequest["position"],
  ) => {
    const firstItem = items[0];
    if (!firstItem) return;
    const canFlipHorizontally =
      items.length > 1 && areShelvesInSameMergedGroup(items.map((item) => item.shelf));

    openMenu({
      event: createMenuAnchorEvent(position),
      menu: {
        title:
          items.length > 1
            ? t("planogramCanvas.itemCount", { count: items.length })
            : firstItem.productCode,
        options: [
          {
            label: t("planogramCanvas.itemMenu.duplicate"),
            keybind: { keyCode: KeyCode.D },
            onClick: () => {
              setIsModified();
              duplicateItemsNextToItem(
                firstItem,
                items.map((item) => item.uniqueId),
                "right",
              );
            },
          },
          {
            label: t("planogramCanvas.itemMenu.unrange"),
            keybind: { keyCode: KeyCode.BACKSPACE },
            onClick: () => {
              unrangeItems(items);
            },
          },
          ...(canFlipHorizontally
            ? [
                {
                  label: t("planogramCanvas.itemMenu.flipHorizontally"),
                  keybind: { keyCode: KeyCode.F },
                  onClick: () => {
                    flipItemsHorizontally(items.map((item) => item.uniqueId));
                  },
                },
              ]
            : []),
          ...(availableStores && items.length === 1
            ? [
                {
                  label: t("planogramCanvas.itemMenu.delete"),
                  keybind: { keyCode: KeyCode.DELETE },
                  onClick: () => {
                    openPogDeleteProductModal({
                      item: firstItem,
                      availableStores,
                    });
                  },
                },
              ]
            : []),
          ...(items.length === 1
            ? [
                {
                  label: t("planogramCanvas.itemMenu.sideView"),
                  keybind: { keyCode: KeyCode.S },
                  onClick: () => {
                    setSideView({
                      bayUniqueId: firstItem.shelf.bay.uniqueId,
                      itemXLeft: firstItem.getCoordinates().xLeft,
                      itemXRight: firstItem.getCoordinates().xRight,
                    });
                  },
                },
              ]
            : []),
        ],
      },
    });
  };

  const openContainerContextMenu = (
    container: PlanogramContainer,
    position: PlanogramContextRequest["position"],
  ) => {
    openMenu({
      event: createMenuAnchorEvent(position),
      menu: {
        title: container.getLabel(),
        options: [
          {
            label: t("planogramCanvas.containerMenu.splitRight"),
            keybind: { ctrl: true, shift: true, keyCode: KeyCode.X },
            onClick: () => {
              splitContainerRight(container);
            },
          },
          {
            label: t("planogramCanvas.containerMenu.splitBottom"),
            keybind: { ctrl: true, shift: true, keyCode: KeyCode.Y },
            onClick: () => {
              splitContainerBottom(container);
            },
          },
          {
            label: t("planogramCanvas.containerMenu.unrange"),
            keybind: { keyCode: KeyCode.U },
            onClick: () => {
              unrangeContainer(container);
            },
          },
          {
            label: t("planogramCanvas.containerMenu.delete"),
            keybind: { keyCode: KeyCode.DELETE },
            onClick: () => {
              deleteContainer(container);
            },
          },
        ],
      },
    });
  };

  const handleContextRequest = (request: PlanogramContextRequest) => {
    handleSelectionChange(request.selection);

    if (request.entity.kind === "container") {
      const container = pog.containers.find(
        (candidate) => candidate.uniqueId === request.entity.id,
      );
      if (container) openContainerContextMenu(container, request.position);
      return;
    }

    if (request.entity.kind === "shelf") {
      const shelf = pog.getShelf(request.entity.id);
      if (shelf) openShelfContextMenu(shelf, createMenuAnchorEvent(request.position));
      return;
    }

    if (request.entity.kind === "bay") {
      const bay = scene.bays.find((candidate) => candidate.id === request.entity.id);
      if (bay) openBayContextMenu(bay, request.position);
      return;
    }

    if (request.entity.kind !== "product") return;
    const items = getSelectedPlanogramItems(request.selection).filter(
      (item): item is PlanogramItem => item instanceof PlanogramItem,
    );
    if (items.length > 0) openItemContextMenu(items, request.position);
  };

  const rootRef = useRef<HTMLDivElement>(null);
  const lastDragPointRef = useRef<{ x: number; y: number } | null>(null);

  const resolveDropWorldPoint = () => {
    const point = lastDragPointRef.current;
    if (!point) return null;
    const stageHost = rootRef.current?.querySelector<HTMLElement>(
      '[data-testid="konva-planogram"]',
    );
    const rect = stageHost?.getBoundingClientRect();
    const stagePoint = rect
      ? {
          x: point.x - rect.left + (stageHost?.scrollLeft ?? 0),
          y: point.y - rect.top + (stageHost?.scrollTop ?? 0),
        }
      : point;
    return viewportModel.stageToWorld(viewport, stagePoint);
  };

  const handleExternalDrop = (payload: DraggedItemsPayload) => {
    setDropPreview(null);
    const worldPoint = resolveDropWorldPoint();
    if (!worldPoint) return;

    const target = resolveRangeTarget(scene, worldPoint, new Set(payload.itemIds));
    if (!target) return;

    if (payload.mode === "copy") {
      if (target.kind === "item") {
        const anchorItem = pog.planogram.getItem(target.itemId);
        if (!anchorItem) return;
        setIsModified();
        duplicateItemsNextToItem(anchorItem, [...payload.itemIds], target.side);
      } else {
        const shelf = pog.getShelf(target.shelfId);
        if (!shelf) return;
        setIsModified();
        duplicateItemsToShelf([...payload.itemIds], shelf);
      }
      return;
    }

    const operation = createItemRangeOperation({
      activeItemId: payload.anchorItemId,
      sourceItemIds: payload.itemIds,
      target,
    });
    const capability = evaluateStandardOperation(pog, operation);
    if (!capability.allowed) return;

    const droppedItems = resolveDraggedItems(pog, [...payload.itemIds]);
    if (droppedItems.length === 0) return;

    if (target.kind === "item") {
      const anchorItem = pog.planogram.getItem(target.itemId);
      if (!(anchorItem instanceof PlanogramItem)) return;
      setIsModified();
      addItemNextToAnotherItem(anchorItem, droppedItems, target.side);
      return;
    }

    const shelf = pog.getShelf(target.shelfId);
    if (!shelf) return;
    setIsModified();
    addItemsToShelf(droppedItems, shelf);
  };

  // Pointer moves must not write React every frame — only when the insertion point changes.
  const [dropPreview, setDropPreview] = useState<PlanogramDropPreview | null>(null);
  const lastDropEvaluationRef = useRef<{ allowed: boolean; key: string; pog: Pog } | null>(null);

  const setDropPreviewIfChanged = (rect: PlanogramDropPreview | null) => {
    setDropPreview((current) => {
      if (current === rect) return current;
      if (
        current &&
        rect &&
        current.height === rect.height &&
        current.width === rect.width &&
        current.x === rect.x &&
        current.y === rect.y
      ) {
        return current;
      }
      return rect;
    });
  };

  const isDropTargetAllowed = (payload: DraggedItemsPayload, target: ItemRangeTarget) => {
    if (payload.mode === "copy") return true;

    const key =
      target.kind === "item"
        ? `${payload.anchorItemId}|item:${target.itemId}:${target.side}`
        : `${payload.anchorItemId}|shelf:${target.shelfId}`;
    const cached = lastDropEvaluationRef.current;
    if (cached && cached.key === key && cached.pog === pog) return cached.allowed;

    const operation = createItemRangeOperation({
      activeItemId: payload.anchorItemId,
      sourceItemIds: payload.itemIds,
      target,
    });
    const allowed = evaluateStandardOperation(pog, operation).allowed;
    lastDropEvaluationRef.current = { allowed, key, pog };
    return allowed;
  };

  const updateDropPreview = () => {
    const payload = getActiveDragPayload();
    if (!payload) {
      setDropPreviewIfChanged(null);
      return;
    }

    const worldPoint = resolveDropWorldPoint();
    if (!worldPoint) {
      setDropPreviewIfChanged(null);
      return;
    }

    const target = resolveRangeTarget(scene, worldPoint, new Set(payload.itemIds));
    if (!target || !isDropTargetAllowed(payload, target)) {
      setDropPreviewIfChanged(null);
      return;
    }

    const size = getDraggedItemWorldSize(pog, payload);
    setDropPreviewIfChanged(size ? getDropPreviewRect(scene, target, worldPoint, size) : null);
  };

  const handleCanvasDragOver = (event: React.PointerEvent<HTMLDivElement>) => {
    lastDragPointRef.current = { x: event.clientX, y: event.clientY };
    onDropOverToCanvas({ onDrop: handleExternalDrop });
    updateDropPreview();
  };

  const handleCanvasDragLeave = () => {
    lastDragPointRef.current = null;
    setDropPreviewIfChanged(null);
  };

  return (
    <EditorRoot
      data-testid="konva-editor-planogram"
      onPointerEnter={handleCanvasDragOver}
      onPointerLeave={handleCanvasDragLeave}
      onPointerMove={handleCanvasDragOver}
      ref={rootRef}
    >
      <KonvaPlanogram
        assetProvider={assetProvider}
        dropPreview={dropPreview}
        labels={labels}
        onActivate={handleActivate}
        onCommit={(commit) => {
          setPog(commit.pog);
          setIsModified();
          handleSelectionChange(commit.selection);
        }}
        onContextRequest={handleContextRequest}
        onHoverChange={handleHoverChange}
        onOverlayBadgeActivate={onOverlayBadgeActivate}
        onSelectionChange={handleSelectionChange}
        onViewportChange={setViewport}
        overlayBadges={overlayBadges}
        overlayChannels={overlayChannels}
        pog={pog}
        scrollToEntityId={scrollToEntityId}
        selection={selection}
        view={{
          productDisplayMode: imagesMode ? "image" : "label",
          productLabel: labelOption,
          scale: baseScale,
        }}
        viewport={viewport}
        viewportConfig={{
          bounds: { maxScale: baseScale * 3, minScale: baseScale * 0.5 },
          defaultState: { scale: baseScale },
        }}
      />
    </EditorRoot>
  );
};

interface GateProps extends Props {
  children: ReactNode;
}

export const KonvaEditorPlanogramGate = ({ children, ...props }: GateProps) => {
  const konvaRenderer = useAtomValue(planogramKonvaRendererAtom);

  return konvaRenderer ? <KonvaEditorPlanogram {...props} /> : <>{children}</>;
};
